import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import css from './BookingSearchSection.module.css';

const SYDNEY_TIME_ZONE = 'Australia/Sydney';

const timeOptions = Array.from({ length: 17 * 4 + 1 }, (_, index) => {
  const totalMinutes = 6 * 60 + index * 15;
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  const hour12 = hour % 12 || 12;
  const period = hour < 12 ? 'am' : 'pm';
  return {
    value,
    label: `${hour12}:${String(minute).padStart(2, '0')} ${period}`,
    totalMinutes,
  };
});

const getSydneyNowParts = () => {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: SYDNEY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());

  return parts.reduce((result, part) => {
    if (part.type !== 'literal') {
      result[part.type] = part.value;
    }
    return result;
  }, {});
};

const todayISO = () => {
  const { year, month, day } = getSydneyNowParts();
  return `${year}-${month}-${day}`;
};

const nextBookableMinuteToday = () => {
  const { hour } = getSydneyNowParts();
  return (Number(hour) + 1) * 60;
};

const nextDateISO = date => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
};

const BookingSearchSection = props => {
  const { sectionId } = props;
  const [date, setDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');

  const availableTimeOptions =
    date === todayISO()
      ? timeOptions.filter(option => option.totalMinutes >= nextBookableMinuteToday())
      : timeOptions;

  const handleSubmit = event => {
    event.preventDefault();
    if (!date || !preferredTime || typeof window === 'undefined') {
      return;
    }

    const query = new URLSearchParams({
      dates: `${date},${nextDateISO(date)}`,
      preferredTime,
    });
    window.location.assign(`/s?${query.toString()}`);
  };

  return (
    <section id={sectionId} className={css.root}>
      <div className={css.inner}>
        <div className={css.copy}>
          <span className={css.eyebrow}>Ready to book?</span>
          <h2>Choose your date and preferred time.</h2>
          <p>We’ll show Sydney makeup artists you can send a request to for that appointment.</p>
        </div>

        <form className={css.form} onSubmit={handleSubmit}>
          <label className={css.field}>
            <span>Date</span>
            <input
              type="date"
              value={date}
              min={todayISO()}
              onChange={event => {
                setDate(event.target.value);
                setPreferredTime('');
              }}
              required
            />
          </label>

          <label className={css.field}>
            <span>Preferred time</span>
            <select
              value={preferredTime}
              onChange={event => setPreferredTime(event.target.value)}
              required
              disabled={!date || availableTimeOptions.length === 0}
            >
              <option value="" disabled>
                {!date
                  ? 'Select a date first'
                  : availableTimeOptions.length === 0
                  ? 'No more times today'
                  : 'Select a time'}
              </option>
              {availableTimeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <button
            className={css.submitButton}
            type="submit"
            disabled={!date || !preferredTime || availableTimeOptions.length === 0}
          >
            Browse artists
          </button>
        </form>

        <p className={css.plannedBooking}>
          <FormattedMessage
            id="BookingSearchSection.plannedBooking"
            defaultMessage="Planning ahead? Hotline Glam can also be used for upcoming events, formals and weddings."
          />
        </p>
      </div>
    </section>
  );
};

export default BookingSearchSection;
